import { useState, useEffect } from 'react';
import { Dialog } from '@/components/common/Dialog';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';

interface Props {
  open: boolean;
  editing: { id: string; name: string; rawNames: string[] } | null;
  saving: boolean;
  onSave: (values: { name: string; rawNames: string[] }) => void;
  onClose: () => void;
}

export function CategoryFormDialog({ open, editing, saving, onSave, onClose }: Props) {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [availableNames, setAvailableNames] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setSelectedNames(editing?.rawNames ?? []);

    // Load available raw category names.
    rpc<string[]>('ListRaceCategoryNames').then(setAvailableNames).catch(() => setAvailableNames([]));
  }, [open, editing]);

  const toggleName = (rawName: string) => {
    setSelectedNames((prev) =>
      prev.includes(rawName) ? prev.filter((x) => x !== rawName) : [...prev, rawName]
    );
  };

  const handleSubmit = () => {
    onSave({ name: name.trim(), rawNames: selectedNames });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('crud.editEntity', { entity: t('entity.category') }) : t('crud.newEntity', { entity: t('entity.category') })}
      footer={
        <>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
              hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
          >
            {editing ? t('common.save') : t('common.create')}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
              border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer"
          >
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Category Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-text-muted">{t('categories.name')}</label>
          <input
            type="text"
            placeholder="e.g. Max"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            className="editor-input"
            autoFocus
          />
        </div>

        {/* Category Aliases */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-text-muted">{t('categories.aliases')}</label>
          {availableNames.length === 0 ? (
            <p className="text-xs text-text-muted italic">{t('categories.noAliases')}</p>
          ) : (
            <div className="max-h-[200px] overflow-y-auto rounded-lg border border-border-subtle bg-bg-raised p-2 space-y-1">
              {availableNames.map((rawName) => (
                <label key={rawName} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-bg-hover transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedNames.includes(rawName)}
                    onChange={() => toggleName(rawName)}
                    className="accent-accent"
                  />
                  <span className="text-sm text-text-primary truncate">{rawName}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
